/**
 * ผูก React เข้ากับคิวออฟไลน์ใน IndexedDB
 * ใช้ subscribe แทน polling เพื่อให้ UI อัปเดตทันทีที่สถานะของรายการเปลี่ยน
 */
import { useCallback, useEffect, useState } from 'react';
import { allQueued, type QueuedCapture, type QueueStatus } from '../lib/db';
import {
  discardQueued,
  installQueueTriggers,
  retryNow,
  subscribeQueue,
  syncQueue,
} from '../lib/queue';

export interface QueueSnapshot {
  items: QueuedCapture[];
  counts: Record<QueueStatus, number>;
  /** จำนวนที่ยังส่งไม่สำเร็จ ใช้แสดงตัวเลขบนแถบบน */
  outstanding: number;
}

const EMPTY: QueueSnapshot = {
  items: [],
  counts: { pending: 0, sending: 0, sent: 0, failed: 0 },
  outstanding: 0,
};

export function useQueue(): QueueSnapshot & {
  refresh: () => void;
  sync: () => void;
  retry: (id?: string) => void;
  /** ทิ้งรายการในคิวที่ส่งไม่สำเร็จ — ผู้เรียกต้องถามยืนยันมาก่อนแล้ว */
  discard: (id: string) => Promise<boolean>;
} {
  const [snapshot, setSnapshot] = useState<QueueSnapshot>(EMPTY);

  const refresh = useCallback(() => {
    void allQueued().then((items) => {
      const counts: Record<QueueStatus, number> = {
        pending: 0,
        sending: 0,
        sent: 0,
        failed: 0,
      };
      for (const item of items) counts[item.status] += 1;
      setSnapshot({
        items,
        counts,
        outstanding: counts.pending + counts.sending + counts.failed,
      });
    });
  }, []);

  useEffect(() => {
    refresh();
    const unsubscribe = subscribeQueue(refresh);
    return () => {
      unsubscribe();
    };
  }, [refresh]);

  return {
    ...snapshot,
    refresh,
    sync: () => void syncQueue(),
    retry: (id?: string) => void retryNow(id),
    discard: (id: string) => discardQueued(id),
  };
}

/** ติดตั้งตัวกระตุ้นการส่งคิว เรียกครั้งเดียวที่ระดับ App */
export function useQueueTriggers(): void {
  useEffect(() => installQueueTriggers(), []);
}
